import { Link, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  Box,
  Boxes,
  Calculator,
  ChefHat,
  ClipboardList,
  DollarSign,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Package,
  Receipt,
  RefreshCw,
  ScanBarcode,
  Send,
  Shield,
  ShoppingCart,
  Store,
  Tag,
  Ticket,
  TrendingUp,
  Truck,
  Upload,
  UserCog,
  Users,
  Wifi,
} from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { Action } from '@/shared/constants/actions'
import type { LucideIcon } from 'lucide-react'
import type nav from '@/i18n/locales/en/nav.json'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useUnassignedCount } from '@/features/categories/hooks/use-unassigned-count'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

type NavKey = keyof typeof nav

interface NavItem {
  to: string
  labelKey: NavKey
  icon: LucideIcon
  /** A single required action, or a list where any one of them grants visibility. */
  requireAction?: Action | Array<Action>
  /**
   * Marks the entry as carrying an open-work count. Only Kategorien uses it today: the assignment
   * worklist lives behind a tab nobody discovers on their own, and a badge is the cheapest way to
   * make it findable without adding a navigation entry that would sit there even when empty.
   */
  badge?: 'unassignedCategories'
}

type NavEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'section'; labelKey: NavKey; items: Array<NavItem> }

const navigation: Array<NavEntry> = [
  {
    type: 'item',
    item: {
      to: '/',
      labelKey: 'dashboard',
      icon: LayoutDashboard,
      requireAction: ACTIONS.OVERVIEW_VIEW,
    },
  },
  {
    type: 'section',
    labelKey: 'catalog',
    items: [
      {
        to: '/products',
        labelKey: 'products',
        icon: Package,
        requireAction: ACTIONS.PRODUCT_VIEW,
      },
      {
        to: '/categories',
        labelKey: 'categories',
        icon: Tag,
        requireAction: ACTIONS.CATEGORY_VIEW,
        badge: 'unassignedCategories',
      },
      {
        to: '/pricing',
        labelKey: 'pricing',
        icon: DollarSign,
        requireAction: ACTIONS.PRICE_VIEW,
      },
    ],
  },
  {
    type: 'section',
    labelKey: 'pos',
    items: [
      {
        to: '/pos/layout',
        labelKey: 'posLayout',
        icon: LayoutGrid,
        requireAction: ACTIONS.POS_LAYOUT_VIEW,
      },
      {
        to: '/pos/staff',
        labelKey: 'posStaff',
        icon: Users,
        requireAction: ACTIONS.STAFF_VIEW,
      },
      {
        to: '/pos/vouchers',
        labelKey: 'posVouchers',
        icon: Ticket,
        requireAction: ACTIONS.VOUCHER_VIEW,
      },
    ],
  },
  {
    type: 'section',
    labelKey: 'operations',
    items: [
      {
        to: '/inventory',
        labelKey: 'inventory',
        icon: Boxes,
        requireAction: ACTIONS.INVENTORY_VIEW,
      },
      {
        to: '/inventory/deliveries',
        labelKey: 'deliveries',
        icon: Truck,
        requireAction: [ACTIONS.INVENTORY_DELIVERY, ACTIONS.INVENTORY_VIEW],
      },
      {
        to: '/inventory/purchase-orders',
        labelKey: 'purchaseOrders',
        icon: Send,
        requireAction: ACTIONS.PURCHASE_ORDER_VIEW,
      },
      {
        to: '/inventur',
        labelKey: 'countSessions',
        icon: ScanBarcode,
        requireAction: [
          ACTIONS.INVENTORY_COUNT_SESSION,
          ACTIONS.INVENTORY_VIEW,
        ],
      },
      {
        to: '/transactions',
        labelKey: 'transactions',
        icon: Receipt,
        requireAction: ACTIONS.TRANSACTION_VIEW,
      },
      {
        to: '/sales',
        labelKey: 'sales',
        icon: BarChart3,
        requireAction: ACTIONS.TRANSACTION_VIEW,
      },
      {
        to: '/pickups',
        labelKey: 'pickups',
        icon: ClipboardList,
        requireAction: ACTIONS.TRANSACTION_VIEW,
      },
      {
        // Menüplanung is day-to-day operations, not an evaluation. As a loose item after
        // Auswertungen it read as part of that section.
        to: '/menus',
        labelKey: 'menus',
        icon: ChefHat,
        requireAction: ACTIONS.MENU_VIEW,
      },
    ],
  },
  {
    // CHG-2026-09-manager-reporting. Deliberately its own section rather than two more entries
    // under Betrieb: these answer the owner's questions, while the entries above are the
    // operational record of individual transactions.
    //
    // "Ohne Warengruppe" is not an entry here. It links into the Kategorien → Nicht zugeordnet tab
    // that already exists, which is also where the sidebar badge points, rather than duplicating
    // one worklist under two names.
    type: 'section',
    labelKey: 'analytics',
    items: [
      {
        to: '/reports/sales',
        labelKey: 'reportsSales',
        icon: TrendingUp,
        requireAction: ACTIONS.REPORT_SALES_VIEW,
      },
      {
        to: '/reports/articles',
        labelKey: 'reportsArticles',
        icon: ScanBarcode,
        requireAction: ACTIONS.REPORT_ARTICLES_VIEW,
      },
      {
        to: '/reports/order-proposal',
        labelKey: 'reportsOrderProposal',
        icon: ShoppingCart,
        requireAction: ACTIONS.REPORT_ORDER_PROPOSAL_VIEW,
      },
      {
        to: '/reports/accounting',
        labelKey: 'reportsAccounting',
        icon: Calculator,
        requireAction: ACTIONS.REPORT_ACCOUNTING_VIEW,
      },
    ],
  },
  {
    type: 'section',
    labelKey: 'admin',
    items: [
      {
        to: '/users',
        labelKey: 'users',
        icon: UserCog,
        requireAction: ACTIONS.USER_MANAGE,
      },
      {
        to: '/customers',
        labelKey: 'customers',
        icon: Users,
        requireAction: ACTIONS.CUSTOMER_VIEW,
      },
      {
        to: '/roles',
        labelKey: 'roles',
        icon: Shield,
        requireAction: ACTIONS.ROLE_PERMISSION_MANAGE,
      },
      {
        to: '/restaurants',
        labelKey: 'locations',
        icon: Store,
        requireAction: ACTIONS.RESTAURANT_VIEW,
      },
      {
        to: '/bundles',
        labelKey: 'bundles',
        icon: Box,
        requireAction: ACTIONS.BUNDLE_PUBLISH,
      },
      {
        to: '/webbill-sync',
        labelKey: 'webbillSync',
        icon: RefreshCw,
        requireAction: ACTIONS.CATALOG_IMPORT,
      },
      {
        to: '/integrations',
        labelKey: 'integrations',
        icon: KeyRound,
        requireAction: ACTIONS.INTEGRATIONS_MANAGE,
      },
      {
        to: '/pricing/import',
        labelKey: 'pricingImport',
        icon: Upload,
        requireAction: ACTIONS.PRICING_IMPORT,
      },
      {
        to: '/edges',
        labelKey: 'edges',
        icon: Wifi,
        requireAction: ACTIONS.EDGE_VIEW,
      },
    ],
  },
]

interface SidebarProps {
  onNavigate?: () => void
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const { logout, user } = useAuth()
  const { can } = usePermissions()
  const { t: tApp } = useTranslation('app')
  const { t: tNav } = useTranslation('nav')
  const { t: tCommon } = useTranslation('common')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const unassignedCount = useUnassignedCount()

  // Longest matching entry wins, so /inventory/deliveries lights up only Lieferungen even though
  // /inventory is its path prefix, while /inventory/history still lights up Inventar.
  const activePath = navigation
    .flatMap((entry) => (entry.type === 'section' ? entry.items : [entry.item]))
    .filter(
      (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
    )
    .reduce<string | null>(
      (best, item) =>
        best === null || item.to.length > best.length ? item.to : best,
      null,
    )

  const canAccess = (item: NavItem): boolean => {
    if (!item.requireAction) return true
    return Array.isArray(item.requireAction)
      ? item.requireAction.some((action) => can(action))
      : can(item.requireAction)
  }

  const renderLink = (item: NavItem) => {
    const Icon = item.icon
    const isActive = item.to === activePath
    // Only shown when there is something to do, and it disappears once the list is clean — which a
    // permanent navigation entry could not.
    const count = item.badge === 'unassignedCategories' ? unassignedCount : 0
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-accent transition-colors${
          isActive ? ' bg-accent text-accent-foreground' : ''
        }`}
        onClick={onNavigate}
      >
        <Icon className="w-5 h-5" />
        <span className="flex-1">{tNav(item.labelKey)}</span>
        {count > 0 && (
          <Badge variant="secondary" className="tabular-nums">
            {count}
          </Badge>
        )}
      </Link>
    )
  }

  return (
    <div className="flex flex-col h-full bg-card border-r border-border lg:border-r-0">
      <div className="p-6">
        <h1 className="text-xl font-bold">{tApp('title')}</h1>
      </div>

      <Separator />

      <nav className="flex-1 p-4 overflow-y-auto">
        {navigation.map((entry) => {
          if (entry.type === 'item') {
            const { item } = entry
            if (!canAccess(item)) return null
            return renderLink(item)
          }

          const visibleItems = entry.items.filter(canAccess)
          if (visibleItems.length === 0) return null

          return (
            <div key={entry.labelKey} className="mt-5">
              <div className="px-3 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tNav(entry.labelKey)}
              </div>
              <div className="space-y-0.5">{visibleItems.map(renderLink)}</div>
            </div>
          )
        })}
      </nav>

      <Separator />

      <div className="p-4 space-y-2">
        <div className="text-sm text-muted-foreground px-3">{user?.email}</div>
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={logout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          {tCommon('logout')}
        </Button>
      </div>
    </div>
  )
}
